import {
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Tooltip,
  Checkbox,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { pipe, concat, compact } from 'remeda';

import { date as commonDate } from '@finley-technologies/common';

import { CreditAgreementMetadataGQLType } from '../../../../types/dashboard-core-gqlTypes.generated.d';
import CreditAgreementDiff from './CreditAgreementDiff';
import {
  ActiveDiffIcon,
  InfoIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { TextWithCopy } from '~/packages/common-frontend/components/TextWithCopy';

type CreditAgreementTableRowProps = {
  creditAgreementDocumentMetadata: CreditAgreementMetadataGQLType;
  addSelection: Function;
  removeSelection: Function;
  selectDiffDisabled: boolean;
  mostRecentCommitId: string | undefined;
  creditAgreementInfoPathGenerator: (id: string) => string;
};
/**
 * NOTE: Permissioned for lender and admin dash CA configuration
 */
const CreditAgreementTableRow = ({
  creditAgreementDocumentMetadata,
  selectDiffDisabled,
  addSelection,
  removeSelection,
  mostRecentCommitId,
  creditAgreementInfoPathGenerator,
}: CreditAgreementTableRowProps) => {
  const navigate = useNavigate();

  const {
    id,
    version,
    effectiveFrom,
    effectiveTo,
    _note_,
  } = creditAgreementDocumentMetadata;
  return (
    <>
      <Tr>
        <Td textAlign="center">
          <Checkbox
            isDisabled={selectDiffDisabled}
            onChange={(e) => {
              if (!e.target.checked) {
                removeSelection(creditAgreementDocumentMetadata);
              }
              if (e.target.checked) {
                addSelection(creditAgreementDocumentMetadata);
              }
            }}
          />
        </Td>
        <Td>
          <TextWithCopy label="Copy Credit Agreement ID">{id}</TextWithCopy>
        </Td>
        <Td>{version}</Td>
        <Td>{commonDate.epochToLocaleDateString(effectiveFrom)}</Td>
        <Td>
          {id === mostRecentCommitId
            ? '-'
            : commonDate.epochToLocaleDateString(effectiveTo)}
        </Td>
        <Td>{_note_ || '-'}</Td>
        <Td>
          <Flex>
            <InfoIconButton
              ariaLabel="info"
              label="More details"
              onClick={() => navigate(creditAgreementInfoPathGenerator(id))}
            />
          </Flex>
        </Td>
      </Tr>
    </>
  );
};

type CreditAgreementTableProps = {
  creditAgreementDocumentsMetadata: CreditAgreementMetadataGQLType[];
  creditAgreementInfoPathGenerator: (id: string) => string;
};
const CreditAgreementTable = ({
  creditAgreementDocumentsMetadata,
  creditAgreementInfoPathGenerator,
}: CreditAgreementTableProps) => {
  const [selectedCADocumentsForDiff, setSelectedCADocumentsForDiff] = useState<
    [
      CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
      CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
    ]
  >([null, null]);

  const addSelectedCaDocumentsForDiff = useCallback(
    (newSelection: CreditAgreement.DomainEntity.CreditAgreementMetadata) => {
      setSelectedCADocumentsForDiff(
        (
          state: [
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
          ],
        ) => {
          const sortedValues = pipe(state, concat([newSelection]), compact);
          const [ca1, ca2] = sortedValues;
          return [ca1, ca2];
        },
      );
    },
    [setSelectedCADocumentsForDiff],
  );

  const removeSelection = useCallback(
    (
      removedSelection: CreditAgreement.DomainEntity.CreditAgreementMetadata,
    ) => {
      setSelectedCADocumentsForDiff(
        (
          state: [
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
            CreditAgreement.DomainEntity.CreditAgreementMetadata | null,
          ],
        ) => {
          const removedIndex = _.findIndex(
            state,
            (document) => document?.id === removedSelection.id,
          );
          const newState = [...state];
          if (removedIndex !== -1) {
            newState[removedIndex] = null;
          }
          const [ca1, ca2] = newState;
          return [ca1, ca2];
        },
      );
    },
    [setSelectedCADocumentsForDiff],
  );

  const mostRecentCommitId = _.first(creditAgreementDocumentsMetadata)?.id;

  const twoCaSelected = _.every(
    selectedCADocumentsForDiff,
    (caDocMetadata) => !_.isNil(caDocMetadata),
  );

  const selectedCAIds = _.map(
    selectedCADocumentsForDiff,
    (document) => document?.id,
  );

  return (
    <>
      {selectedCADocumentsForDiff[1] && selectedCADocumentsForDiff[0] && (
        <CreditAgreementDiff
          creditAgreementId1={selectedCADocumentsForDiff[0].id}
          creditAgreementId2={selectedCADocumentsForDiff[1].id}
        />
      )}
      <Table>
        <Thead>
          <Tr>
            <Th w="5%" textAlign="center" style={{ whiteSpace: 'nowrap' }}>
              <Flex alignItems="center">
                <Text mr="1">Compare</Text>
                <Tooltip label="Select 2 credit agreement versions to compare">
                  <ActiveDiffIcon width="12" height="12" />
                </Tooltip>
              </Flex>
            </Th>
            <Th>Id</Th>
            <Th w="5%">Version</Th>
            <Th>Effective From</Th>
            <Th>Effective To</Th>
            <Th w="26%">Note</Th>
            <Th w="5%" />
          </Tr>
        </Thead>
        <Tbody>
          {creditAgreementDocumentsMetadata.map(
            (creditAgreementDocumentMetadata) => {
              return (
                <CreditAgreementTableRow
                  key={creditAgreementDocumentMetadata.id}
                  creditAgreementDocumentMetadata={
                    creditAgreementDocumentMetadata
                  }
                  mostRecentCommitId={mostRecentCommitId}
                  selectDiffDisabled={
                    twoCaSelected &&
                    !_.includes(
                      selectedCAIds,
                      creditAgreementDocumentMetadata.id,
                    )
                  }
                  addSelection={addSelectedCaDocumentsForDiff}
                  removeSelection={removeSelection}
                  creditAgreementInfoPathGenerator={
                    creditAgreementInfoPathGenerator
                  }
                />
              );
            },
          )}
        </Tbody>
      </Table>
    </>
  );
};

export default CreditAgreementTable;
